import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';
import authService from './AuthorizeService';
import { formatApiError } from '../../utils/errorFormatter';

interface ChangePasswordModalProps {
    isOpen: boolean;
    toggle: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, toggle }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'currentPassword') setCurrentPassword(value);
        if (name === 'newPassword') setNewPassword(value);
        if (name === 'confirmNewPassword') setConfirmNewPassword(value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmNewPassword) {
            setError('Новый пароль и подтверждение не совпадают.');
            setSuccess(null);
            return;
        }

        if (newPassword.length < 6) {
            setError('Новый пароль должен быть не менее 6 символов.');
            setSuccess(null);
            return;
        }

        setError(null);
        setSuccess(null);

        try {
            const token = await authService.getAccessToken();
            const response = await fetch('/api/Account/ChangePassword', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword }),
            });

            if (response.ok) {
                setSuccess('Пароль успешно изменен!');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                setTimeout(toggle, 2000);
            } else {
                const errorMessage = await formatApiError(response, 'Не удалось изменить пароль');
                setError(errorMessage);
            }
        } catch (error: any) {
            setError(error.message || 'Произошла ошибка при смене пароля');
        }
    };

    return (
        <Dialog open={isOpen} onClose={toggle}>
            <DialogTitle>Сменить пароль</DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <TextField
                        type="password"
                        name="currentPassword"
                        label="Текущий пароль"
                        value={currentPassword}
                        onChange={handleChange}
                        required
                        fullWidth
                        margin="normal"
                        autoComplete="current-password"
                    />
                    <TextField
                        type="password"
                        name="newPassword"
                        label="Новый пароль"
                        value={newPassword}
                        onChange={handleChange}
                        required
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                    />
                    <TextField
                        type="password"
                        name="confirmNewPassword"
                        label="Подтвердите новый пароль"
                        value={confirmNewPassword}
                        onChange={handleChange}
                        required
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={toggle}>Отмена</Button>
                <Button onClick={handleSubmit} variant="contained">Сменить пароль</Button>
            </DialogActions>
        </Dialog>
    );
};
